/**
 * THE TRACK LIBRARY — every card the game can deal, read from the game's own data.
 *
 * ⚠ THIS USED TO READ SUPABASE, AND THAT WAS THE BUG. Howard, 21 Aug 2026:
 * *"i dont see the 2025 tracks we inserted today in my library of tracks."*
 *
 * He was right, and the reason is the failure this project keeps naming: THERE
 * WERE TWO LIBRARIES. `src/data/track-cards.json` is what the engine races on
 * and had all 58 cards the moment D-EZ-75 landed. The `tracks` table in Supabase
 * was a separate transcription, made before the race engine existed, and it
 * still held 31 — 8 from the 2000s set and 23 from 2014. Nothing else in the
 * app read it; it fed this screen and only this screen. So the cards were in the
 * game and absent from the page that exists to list them.
 *
 * Inserting 27 rows would have fixed it until the next card set. Reading the one
 * file the engine reads fixes it for good: a card that can be raced on is now,
 * by construction, a card that appears here. There is nothing left to keep in
 * sync, and this screen works with the network off.
 *
 * `lib/content.ts` and the `tracks` table are left where they are — the table is
 * a historical transcription and deleting data is not a thing to do casually —
 * but nothing imports `listTracks` any more.
 */

import { useMemo, useState } from 'react';
import { TRACK_CARDS, type TrackCard } from '../domain/turn.ts';
import { NAMED_RACES } from '../domain/schedule.ts';
import { TrackMark } from './RaceLogo.tsx';
import type { HistorySeason } from '../domain/history/index.ts';
import TrackHistoryPanel from './story/TrackHistoryPanel.tsx';

/** Newest set first, then by name — the shelf as somebody would stack it. */
const BY_SET_THEN_NAME = (a: TrackCard, b: TrackCard): number =>
  (b.cardYear ?? 0) - (a.cardYear ?? 0) || a.name.localeCompare(b.name);

const setLabel = (year: number | null | undefined): string =>
  year && year > 0 ? String(year) : 'undated';

/** Every named race a card prints — one, or two on the fourteen that print two. */
const eventsOf = (id: string): string[] =>
  NAMED_RACES.filter((r) => r.trackId === id).map((r) => `${r.raceName} · ${r.turns}t`);

export interface TrackContentProps {
  /**
   * D-EZ-102 — the league's seasons (settled + the one being raced), for the
   * track histories above the card table. Absent: the card table alone.
   */
  seasons?: readonly HistorySeason[];
}

export default function TrackContent({ seasons }: TrackContentProps = {}) {
  const [set, setSet] = useState<string>('all');
  const [category, setCategory] = useState<string>('all');

  const sets = useMemo(
    () => [...new Set(TRACK_CARDS.map((t) => setLabel(t.cardYear)))].sort().reverse(),
    [],
  );
  const categories = useMemo(() => [...new Set(TRACK_CARDS.map((t) => t.category))].sort(), []);

  const shown = useMemo(
    () =>
      TRACK_CARDS.filter(
        (t) =>
          (set === 'all' || setLabel(t.cardYear) === set) &&
          (category === 'all' || t.category === category),
      ).sort(BY_SET_THEN_NAME),
    [set, category],
  );

  /*
    ⚠ VENUES, NOT CARDS. D-EZ-75: Daytona 2000s, 2014 and 2025 are three cards of
    ONE racetrack, and a season visits it once or twice — never six times. Saying
    both numbers here is the difference between "we have 58 tracks" (false) and
    "58 cards over 30 racetracks" (true, and the thing the calendar cares about).
  */
  const venues = useMemo(() => new Set(TRACK_CARDS.map((t) => t.venue ?? t.id)).size, []);

  return (
    <section className="content" aria-label="Tracks">
      <header className="content__head">
        {/* D-EZ-101 — the tab says Tracks, so the page does too. */}
        <h2>Tracks</h2>
        <p className="dim">
          {TRACK_CARDS.length} cards over {venues} racetracks, transcribed from Red White &amp; Blue
          Racin&rsquo; (PLAAY Games). Every card here can be dealt into a season — a league takes one
          card per racetrack, so Daytona 2025 and Daytona 2014 are the same place, not two of them.
        </p>
      </header>

      {seasons && <TrackHistoryPanel seasons={seasons} />}

      <h2 className="content__cardshead">Track cards</h2>
      <div className="content__filters">
        <label>
          Card set
          <select value={set} onChange={(e) => setSet(e.target.value)}>
            <option value="all">Every set</option>
            {sets.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Type
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">Every type</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <span className="dim">
          {shown.length} of {TRACK_CARDS.length}
        </span>
      </div>

      <div className="trackscroll" tabIndex={0} role="region" aria-label="Track card comparison — scroll sideways for all columns">
      <table>
        <thead>
          <tr>
            <th scope="col">Track</th>
            <th scope="col">Set</th>
            <th scope="col">Type</th>
            <th scope="col" className="num">Miles</th>
            <th scope="col" className="num">Capacity</th>
            <th scope="col" className="num">Esc / Rpr</th>
            <th scope="col">On a 7</th>
            <th scope="col">Races it prints</th>
            <th scope="col">Late-race twist</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((t) => (
            <tr key={t.id}>
              <td className="name">
                <TrackMark trackId={t.id} size={22} />
                {t.name}
              </td>
              <td className="dim">{setLabel(t.cardYear)}</td>
              <td className="dim">{t.category}</td>
              <td className="num">{t.lengthMiles ?? '—'}</td>
              <td className="num">{t.capacity ? t.capacity.toLocaleString() : '—'}</td>
              <td className="num">
                {t.escapeAdj} / {t.repairAdj}
              </td>
              <td>{t.raceCam['7']}</td>
              <td className="dim">{eventsOf(t.id).join('  ·  ')}</td>
              <td className="dim">
                {t.footnotes?.[0]?.text ?? ''}
                {/* The ✻ burst is new to the 2025 set and is a rule nothing else
                    on this row would show. */}
                {t.burst && (
                  <>
                    {t.footnotes?.[0]?.text ? ' ' : ''}
                    <b className="content__burst">✻ {t.burst.text}</b>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}
